<template>
  <div class="chart-container">
    <button @click="closeGraphs" class="close-button" style="background: none; border: none;">
      <span class="material-icons" style="color: #fff;"> cancel_presentation </span>
    </button>

    <Bar
      v-if="hasData"
      :data="chartData"
      :options="chartOptions"
      :height="400"
    />
    <div v-else class="no-data">
      No data available for this period.
    </div>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export default {
  name: 'RFRReasonChart',
  components: { Bar },

  props: {
    data: { type: Array, default: () => [] },
    chartTitle: { type: String, default: 'RFR Reasons' },
    barColor: { type: String, default: '#fff' },
    closeGraphs: { type: Function, default: () => {} }
  },

  computed: {
    hasData() {
      return Array.isArray(this.data) && this.data.length > 0
    }
  },

  data() {
    return {
      chartData: {},
      chartOptions: {}
    }
  },

  watch: {
    data: {
      immediate: true,
      handler() {
        this.buildChart()
      }
    }
  },

  methods: {
        buildChart() {
      if (!this.hasData) {
        this.chartData = {}
        return
      }

      const labels = this.data.map(item => item.RequestReason || 'Unknown')
      const counts = this.data.map(item => item.Count || 0)
      
      // Find the tallest bar
      const maxBar = Math.max(...counts)
      const total = counts.reduce((sum, count) => sum + count, 0)

      this.chartData = {
        labels,
        datasets: [{
          label: this.chartTitle,
          data: counts,
          backgroundColor: this.barColor,
          borderColor: '#93c5fd',
          borderWidth: 1,
          borderRadius: 4
        }]
      }

      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,

        plugins: {
          legend: {
            position: 'top',
            labels: { color: '#fff', font: { size: 16 }, padding: 20 }
          },
          title: {
            display: true,
            text: this.chartTitle,
            color: '#fff',
            font: { size: 18, weight: '600' },
            padding: { bottom: 30 }
          },
          tooltip: {
            backgroundColor: '#1f2937',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#4b5563',
            borderWidth: 1,
            padding: 12,
            displayColors: false,
            callbacks: {
              title: (tooltipItems) => tooltipItems[0].label,
              label: (context) => {
                const value = context.raw
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0
                return [
                  `Count: ${value}`,
                  `Percentage: ${percentage}%`,
                  // `Total: ${total}`
                ]
              }
            }
          }
        },

        scales: {
          y: {
            beginAtZero: true,
            max: Math.ceil(maxBar),   // ← Key change: scale to tallest bar + 8% padding
            grid: { color: '#374151', lineWidth: 0.8 },
            ticks: {
              color: '#fff',
              font: { size: 13 },
              stepSize: Math.max(1, Math.floor(maxBar / 8))
            },
            title: {
              display: true,
              text: 'Number of Requests',
              color: '#fff',
              font: { size: 13 }
            }
          },
          x: {
            grid: { color: '#374151', lineWidth: 0.8 },
            ticks: {
              color: '#fff',
              font: { size: 12 },
              maxRotation: 45,
              minRotation: 45
            }
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 500px;
  margin: 20px 0 50px;
  padding-bottom: 40px;
}

.no-data {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-style: italic;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}
</style>